import { eq, or } from 'tio-ui/utilities';
import { get } from '@ember/object';
import {
  getSortedOptionsByFieldName,
  type CustomFieldId,
  type TemplateFieldName,
} from '../../utils/tuition-assistance/fields.ts';
import { hash } from '@ember/helper';
import { normalizeDate, formatDateToISO } from '../../utils/date/index.ts';
import Input from 'tio-reimagined/components/input';
import RadioGroup from 'tio-reimagined/components/radio-group';
import Textarea from 'tio-reimagined/components/textarea';
import ReSelect from 'tio-reimagined/components/select';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { tracked } from '@glimmer/tracking';
import { uniqueId } from '@ember/helper';
import Component from '@glimmer/component';
import inputmask from '../../modifiers/inputmask.ts';
import { getInputmaskUnmaskedValue } from '../../utils/inputmask.ts';
import { PhCalendarBlank } from 'ember-phosphor-icons';
import type { CustomFieldSignature } from '../../types/tuition-assistance.ts';
import type { TASProgramTemplateModelFieldsSignature } from '../../models/tas-program-template.ts';
import type Owner from '@ember/owner';
import type TasProgramTemplateModel from '../../models/tas-program-template.ts';

interface TuitionAssistanceProgramCustomFormFieldSignature {
  Args: {
    didUpdate: (value: CustomFieldSignature) => void;
    errors: string[];
    field: CustomFieldSignature;
    hasSubmitted?: boolean;
    programTemplate?: TasProgramTemplateModel;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class TuitionAssistanceProgramCustomFormFieldComponent extends Component<TuitionAssistanceProgramCustomFormFieldSignature> {
  @tracked customFieldObject: CustomFieldSignature;

  constructor(owner: Owner, args: TuitionAssistanceProgramCustomFormFieldSignature['Args']) {
    super(owner, args);

    this.customFieldObject = { ...this.args.field };
  }

  updateSingleValue = (value: string | boolean | number, event?: Event) => {
    let valueToSet = value;

    // Handle currency input - convert to cents for storage
    if (this.args.field.inputType === 'currency' && event?.target) {
      const unmaskedValue = getInputmaskUnmaskedValue(event);
      if (unmaskedValue !== undefined) {
        valueToSet = Math.round(Number(unmaskedValue) * 100);
      }
    }

    // Handle date normalization
    if (this.args.field.inputType === 'date' && typeof valueToSet === 'string') {
      const normalized = normalizeDate(valueToSet as string);
      if (normalized) {
        valueToSet = normalized;
      }
    }

    const values: (string | boolean | number)[] = [valueToSet];
    this.customFieldObject = { ...this.customFieldObject, values: values as string[] | boolean[] };
    this.args.didUpdate?.(this.customFieldObject);
  };

  updateDateValue = (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const isoDate = formatDateToISO(selectedDates[0]);
      const values: string[] = [isoDate];
      this.customFieldObject = { ...this.customFieldObject, values };
      this.args.didUpdate?.(this.customFieldObject);
    }
  };

  get fieldName() {
    const { name } = this.args.field;

    if (!name || typeof name !== 'string') {
      return undefined;
    }

    const programTemplate = this.args.programTemplate;

    if (programTemplate && name in programTemplate) {
      return name as keyof TASProgramTemplateModelFieldsSignature;
    }

    return undefined;
  }

  /**
   * Gets the field identifier for looking up options from tasFieldOptions.
   * Custom fields use `id`, standard fields use `name`.
   */
  get fieldIdentifier() {
    // Prioritize id for custom fields, fallback to name for standard fields
    const identifier = this.args.field.id || this.args.field.name;

    if (!identifier || typeof identifier !== 'string') {
      return undefined;
    }

    return identifier as TemplateFieldName | CustomFieldId;
  }

  get fieldOptions() {
    if (!this.args.programTemplate || !this.fieldIdentifier) return [];

    const templateOptions = this.args.programTemplate.tasFieldOptions;
    return getSortedOptionsByFieldName(this.fieldIdentifier, templateOptions || []);
  }

  get templateField() {
    if (!this.args.programTemplate || !this.args.field.id) return null;
    return this.args.programTemplate.customFields?.find((field) => field.id === this.args.field.id);
  }

  get isRequired() {
    // Check template first (source of truth), fallback to instance field
    return this.templateField?.required ?? this.args.field.required;
  }

  get shouldUseFieldOptions() {
    // Check template first (source of truth), fallback to instance field
    return this.templateField?.value_as_field_option ?? this.args.field.value_as_field_option;
  }

  get selectItems() {
    if (this.args.field.input !== 'select') return [];

    // For select fields using TAS field options (template as source of truth)
    if (this.shouldUseFieldOptions && this.args.programTemplate && this.fieldOptions.length > 0) {
      return this.fieldOptions.map((option) => ({
        key: String(option.value),
        value: String(option.value),
      }));
    }

    // For custom options (user context, options stored in @field.options)
    if (
      this.args.field.options &&
      Array.isArray(this.args.field.options) &&
      this.args.field.options.length > 0
    ) {
      return this.args.field.options.map((option) => ({
        key: typeof option === 'string' ? option : String(option.value),
        value: typeof option === 'string' ? option : String(option.value),
      }));
    }

    // Fallback: Check tasFieldOptions using field ID (for custom fields with empty inline options)
    if (this.args.programTemplate && this.fieldOptions.length > 0) {
      return this.fieldOptions.map((option) => ({
        key: String(option.value),
        value: String(option.value),
      }));
    }

    return [];
  }

  get displayValue() {
    const singleValue = this.args.field.values[0];

    // Handle currency display - convert from cents to dollars
    if (this.args.field.inputType === 'currency' && typeof singleValue === 'number') {
      return singleValue / 100;
    }

    return singleValue;
  }

  <template>
    <div ...attributes>
      {{#let
        (hash
          isVisible=@field.visible
          isRequired=this.isRequired
          programLabel=@field.label
          inputId=(uniqueId)
          field=@field
          options=@field.options
          singleValue=(get @field.values "0")
          values=@field.values
        )
        as |config|
      }}
        {{#if config.isVisible}}
          <label for={{config.inputId}} class="font-semibold text-sm text-tio-gray-700 block mb-1">
            {{config.programLabel}}
            {{#if config.isRequired}}
              <span class="text-red-500 ml-0.5">*</span>
            {{/if}}
          </label>

          {{! Textarea input }}
          {{#if (eq @field.input "textarea")}}
            <Textarea
              data-test-custom-field-textarea
              id={{config.inputId}}
              @value={{config.singleValue}}
              @onChange={{this.updateSingleValue}}
              @errors={{@errors}}
            />
          {{/if}}

          {{! Simple text input }}
          {{#if (eq @field.input "input")}}
            {{#if (eq @field.inputType "currency")}}
              {{! Currency input with inputmask }}
              <Input
                data-test-custom-field-input-currency
                id={{config.inputId}}
                placeholder="$0.00"
                @value={{this.displayValue}}
                @onChange={{this.updateSingleValue}}
                @errors={{@errors}}
                {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2"}}
              />
            {{else if (eq @field.inputType "date")}}
              {{! Date input }}
              <div class="relative w-full">
                <EmberFlatpickr
                  {{! @glint-expect-error: singleValue type is broader than DateOption }}
                  @date={{or config.singleValue ""}}
                  @onChange={{this.updateDateValue}}
                  @dateFormat="Y-m-d"
                  @altInput={{true}}
                  @altFormat="F j, Y"
                  @disableMobile={{true}}
                  placeholder="mm/dd/yyyy"
                  data-test-custom-field-input-date
                  class="tio-form-input-lg"
                />
                <div
                  class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                >
                  <PhCalendarBlank @size="20" />
                </div>
              </div>
            {{else}}
              {{! Simple string input }}
              <Input
                data-test-custom-field-input
                id={{config.inputId}}
                @value={{config.singleValue}}
                @onChange={{this.updateSingleValue}}
                @errors={{@errors}}
              />
            {{/if}}
          {{/if}}

          {{! Radio input }}
          {{#if (eq @field.input "radio")}}
            <RadioGroup
              {{! @glint-expect-error: config.singleValue is unknown type but RadioGroup expects string/boolean }}
              @value={{config.singleValue}}
              @orientation="vertical"
              @onChange={{this.updateSingleValue}}
              @errors={{@errors}}
              @label=""
              class="p-2"
              as |Radio|
            >
              {{#each config.options as |option index|}}
                <Radio
                  @label={{option.label}}
                  @value={{option.value}}
                  data-test-custom-radio-option={{index}}
                />
              {{/each}}
            </RadioGroup>
          {{/if}}

          {{! Select input }}
          {{#if (eq @field.input "select")}}
            <ReSelect
              data-test-custom-field-select
              id={{config.inputId}}
              @items={{this.selectItems}}
              {{! @glint-expect-error: customFieldObject.values[0] type is broader than string }}
              @selected={{get this.customFieldObject.values "0"}}
              @onChange={{this.updateSingleValue}}
              @errors={{@errors}}
              @placeholder="Select an option"
            />
          {{/if}}
        {{/if}}
      {{/let}}
    </div>
  </template>
}
